import { useState, useEffect } from 'react';
import {
  Bot, Save, Check, AlertCircle, FileText, Receipt, UserX, Calendar,
  RefreshCw, Loader2, Wrench
} from '../../icons/wp-icons';
import { proaktiveAktionenApi } from '../../api/proaktiveAktionenApi';

function ProaktiveAktionenSettings({ readOnly = false }) {
  // Einstellungen
  const [aktiviert, setAktiviert] = useState(true);
  const [kvaNachfassenNachTagen, setKvaNachfassenNachTagen] = useState(7);
  const [rechnungMahnungNachTagen, setRechnungMahnungNachTagen] = useState(14);
  const [kundeInaktivNachMonaten, setKundeInaktivNachMonaten] = useState(6);
  const [terminErinnerungVorTagen, setTerminErinnerungVorTagen] = useState(1);
  const [wartungErinnerungAktiv, setWartungErinnerungAktiv] = useState(true);
  const [wartungErinnerungVorTagen, setWartungErinnerungVorTagen] = useState(30);

  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  useEffect(() => {
    loadEinstellungen();
  }, []);

  const loadEinstellungen = async () => {
    try {
      setLoading(true);
      const response = await proaktiveAktionenApi.getEinstellungen();
      const data = response.data;

      setAktiviert(data.aktiviert ?? true);
      setKvaNachfassenNachTagen(data.kvaNachfassenNachTagen ?? 7);
      setRechnungMahnungNachTagen(data.rechnungMahnungNachTagen ?? 14);
      setKundeInaktivNachMonaten(data.kundeInaktivNachMonaten ?? 6);
      setTerminErinnerungVorTagen(data.terminErinnerungVorTagen ?? 1);
      setWartungErinnerungAktiv(data.wartungErinnerungAktiv ?? true);
      setWartungErinnerungVorTagen(data.wartungErinnerungVorTagen ?? 30);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await proaktiveAktionenApi.updateEinstellungen({
        aktiviert,
        kvaNachfassenNachTagen,
        rechnungMahnungNachTagen,
        kundeInaktivNachMonaten,
        terminErinnerungVorTagen,
        wartungErinnerungAktiv,
        wartungErinnerungVorTagen
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleManuellGenerieren = async () => {
    setGenerating(true);
    setGenerateResult(null);
    try {
      const response = await proaktiveAktionenApi.manuellGenerieren();
      setGenerateResult({
        success: true,
        message: response.data.message,
        details: response.data.details
      });
    } catch (err) {
      setGenerateResult({
        success: false,
        message: err.response?.data?.error || err.message
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg shrink-0">
            <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-semibold">Proaktive KI-Vorschläge</h2>
            <p className="text-xs sm:text-sm text-text-muted">
              Automatische Handlungsempfehlungen im Dashboard
            </p>
          </div>
          <label className={`relative inline-flex items-center ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={aktiviert}
              onChange={(e) => !readOnly && setAktiviert(e.target.checked)}
              disabled={readOnly}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary ${readOnly ? 'opacity-60' : ''}`}></div>
          </label>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">x</button>
          </div>
        )}

        <p className="text-sm text-text-muted">
          Die KI analysiert täglich um 06:00 Uhr Ihre Daten und erstellt Handlungsvorschläge,
          die im Dashboard-Widget angezeigt werden.
        </p>

        {/* Regel-Einstellungen */}
        <div className="space-y-3 sm:space-y-4">
          {/* KVA Nachfassen */}
          <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-4 h-4 text-blue-600 shrink-0" />
              <span className="font-medium text-sm sm:text-base">KVA nachfassen</span>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Vorschlag, wenn ein versendeter Kostenvoranschlag nicht beantwortet wurde.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-muted">Nach</label>
              <input
                type="number"
                min="1"
                max="60"
                value={kvaNachfassenNachTagen}
                onChange={(e) => setKvaNachfassenNachTagen(parseInt(e.target.value) || 7)}
                disabled={readOnly || !aktiviert}
                className="w-20 px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100 disabled:text-text-muted"
              />
              <span className="text-sm text-text-muted">Tagen ohne Antwort</span>
            </div>
          </div>

          {/* Rechnungs-Mahnung */}
          <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Receipt className="w-4 h-4 text-red-600 shrink-0" />
              <span className="font-medium text-sm sm:text-base">Zahlungserinnerung</span>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Vorschlag für eine Mahnung bei überfälligen Rechnungen.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-muted">Nach</label>
              <input
                type="number"
                min="1"
                max="90"
                value={rechnungMahnungNachTagen}
                onChange={(e) => setRechnungMahnungNachTagen(parseInt(e.target.value) || 14)}
                disabled={readOnly || !aktiviert}
                className="w-20 px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100 disabled:text-text-muted"
              />
              <span className="text-sm text-text-muted">Tagen Zahlungsverzug</span>
            </div>
          </div>

          {/* Inaktive Kunden */}
          <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <UserX className="w-4 h-4 text-orange-600 shrink-0" />
              <span className="font-medium text-sm sm:text-base">Inaktive Kunden</span>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Vorschlag zur Kontaktaufnahme bei Kunden ohne aktuelle Aktivität.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-muted">Nach</label>
              <input
                type="number"
                min="1"
                max="24"
                value={kundeInaktivNachMonaten}
                onChange={(e) => setKundeInaktivNachMonaten(parseInt(e.target.value) || 6)}
                disabled={readOnly || !aktiviert}
                className="w-20 px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100 disabled:text-text-muted"
              />
              <span className="text-sm text-text-muted">Monaten Inaktivität</span>
            </div>
          </div>

          {/* Termin-Erinnerung */}
          <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-4 h-4 text-purple-600 shrink-0" />
              <span className="font-medium text-sm sm:text-base">Termin-Erinnerung</span>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Tägliche Zusammenfassung der anstehenden Termine.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="7"
                value={terminErinnerungVorTagen}
                onChange={(e) => setTerminErinnerungVorTagen(parseInt(e.target.value) || 1)}
                disabled={readOnly || !aktiviert}
                className="w-20 px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100 disabled:text-text-muted"
              />
              <span className="text-sm text-text-muted">Tag(e) im Voraus</span>
            </div>
          </div>

          {/* Wartungserinnerung */}
          <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Wrench className="w-4 h-4 text-teal-600 shrink-0" />
              <span className="font-medium text-sm sm:text-base flex-1">Wartungserinnerung</span>
              <label className={`relative inline-flex items-center ${readOnly || !aktiviert ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={wartungErinnerungAktiv}
                  onChange={(e) => !readOnly && aktiviert && setWartungErinnerungAktiv(e.target.checked)}
                  disabled={readOnly || !aktiviert}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary ${readOnly || !aktiviert ? 'opacity-60' : ''}`}></div>
              </label>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Vorschlag, wenn eine Wartung laut Wartungsvertrag bald fällig ist. Gesetzliche Wartungen werden mit hoher Priorität angezeigt.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="7"
                max="90"
                value={wartungErinnerungVorTagen}
                onChange={(e) => setWartungErinnerungVorTagen(parseInt(e.target.value) || 30)}
                disabled={readOnly || !aktiviert || !wartungErinnerungAktiv}
                className="w-20 px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100 disabled:text-text-muted"
              />
              <span className="text-sm text-text-muted">Tage vor Fälligkeit</span>
            </div>
          </div>
        </div>

        {/* Manuell generieren */}
        {!readOnly && (
          <div className="pt-4 border-t border-border">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div>
                <p className="text-sm font-medium">Vorschläge jetzt generieren</p>
                <p className="text-xs text-text-muted">
                  Erstellt sofort neue Vorschläge basierend auf aktuellen Daten
                </p>
              </div>
              <button
                onClick={handleManuellGenerieren}
                disabled={generating || !aktiviert}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-50 transition disabled:opacity-50 text-sm"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Jetzt generieren
              </button>
            </div>

            {generateResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${
                generateResult.success
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                <p className="font-medium">{generateResult.message}</p>
                {generateResult.details && (
                  <ul className="mt-2 text-xs space-y-1">
                    <li>KVAs: {generateResult.details.kvas}</li>
                    <li>Rechnungen: {generateResult.details.rechnungen}</li>
                    <li>Inaktive Kunden: {generateResult.details.inaktiveKunden}</li>
                    <li>Termine: {generateResult.details.termine}</li>
                    {generateResult.details.wartungen !== undefined && (
                      <li>Wartungen: {generateResult.details.wartungen}</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Speichern Button */}
        {!readOnly && (
          <div className="pt-4 border-t border-border">
            {saved && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                <Check className="w-4 h-4" />
                Einstellungen gespeichert
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Einstellungen speichern
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProaktiveAktionenSettings;
